/**
 * Alarm configuration for the Live Monitor — what alarms, at what priority,
 * with which message, after what delay, sounding how.
 *
 * Modelled on IEC 60601-1-8 (alarm systems in medical electrical equipment):
 *
 *   PRIORITY     visual indicator                         auditory burst
 *   high   !!!   red,    flashing 1.4–2.8 Hz              10 pulses (3+2, 3+2), repeat 2.5–15 s
 *   medium !!    yellow, flashing 0.4–0.8 Hz               3 pulses,            repeat 2.5–30 s
 *   low    !     cyan/yellow, steady                       1–2 pulses,          repeat >15 s or none
 *
 * The standard encodes PRIORITY in the burst and (optionally) the alarm
 * CATEGORY in the melody. It does not give every condition its own sound —
 * operators cannot learn dozens of them. So a rule picks a priority and a
 * category sound; "different sound per abnormality" is achieved by category
 * (cardiac vs oxygen vs temperature vs equipment) × priority.
 *
 * Two delays are distinguished, as the standard requires them to be disclosed:
 *   delaySec     ALARM CONDITION DELAY — how long the condition must persist
 *                before it is declared (debounces artefact).
 *   repeatSec    interburst interval of the auditory signal.
 *   audible      false = this alarm never sounds for anyone (it still shows,
 *                blinks and is recorded). The per-alarm mute for the whole unit.
 *   maxSoundSec  0 = the signal repeats until the condition clears or is
 *                acknowledged (normal for physiological alarms); >0 stops the
 *                SOUND after that long while the visual indication stays
 *                (used for technical "patient not connected" alarms that can
 *                last for hours).
 *
 * The rule set is editable by any user from the Alarm settings panel. Every
 * save is versioned on the server (who / when) so changes are traceable;
 * a fresh install or "Reset to defaults" yields DEFAULT_RULES below.
 *
 * NOTE for the quality file: these defaults are engineering defaults in the
 * range used by bedside monitors. They must be reviewed and signed off by the
 * clinical lead before clinical use, and a browser on arbitrary PC speakers
 * cannot by itself be verified for the standard's sound-pressure requirements.
 */
import axios from 'axios';
import { liveApi } from '../../config/config';

export const CONFIG_VERSION = 1;
const LS_KEY = 'lm.alarmConfig';

// ───────────────────────── IEC 60601-1-8 constants ─────────────────────────
export const PRIORITIES = {
  high:   { label: 'High',   mark: '!!!', severity: 'critical', rank: 0, repeat: [2.5, 15], repeatDefault: 8,  colour: 'red, flashing 2 Hz',      burst: '10 pulses (3+2, 3+2)' },
  medium: { label: 'Medium', mark: '!!',  severity: 'warning',  rank: 1, repeat: [2.5, 30], repeatDefault: 20, colour: 'yellow, flashing 0.6 Hz', burst: '3 pulses' },
  low:    { label: 'Low',    mark: '!',   severity: 'low',      rank: 2, repeat: [15, 600], repeatDefault: 0,  colour: 'cyan, steady',            burst: '2 pulses' },
};

// Category melodies (IEC 60601-1-8:2006 Annex F). Notes are semitones from C4;
// a high-priority burst plays the 5-note phrase twice, medium plays the first
// three notes, low plays the common two-note "e c".
export const SOUNDS = {
  general:     { label: 'General',            notes: [0, 0, 0, 0, 0] },        // c c c – c c
  cardiac:     { label: 'Cardiac',            notes: [0, 4, 7, 7, 12] },       // c e g – g C
  oxygen:      { label: 'Oxygenation',        notes: [12, 11, 9, 7, 5] },      // C b a – g f
  ventilation: { label: 'Ventilation',        notes: [0, 9, 5, 9, 5] },        // c a f – a f
  temperature: { label: 'Temperature',        notes: [0, 2, 4, 5, 7] },        // c d e – f g
  equipment:   { label: 'Equipment / power',  notes: [12, 0, 0, 12, 0] },      // C c c – C c
};
export const LOW_NOTES = [4, 0];                                                // e c

export const PARAM_OPTIONS = [
  { key: 'hr',         label: 'Heart rate',          unit: 'bpm', sound: 'cardiac' },
  { key: 'spo2',       label: 'SpO2',                unit: '%',   sound: 'oxygen' },
  { key: 'resp',       label: 'Respiration',         unit: 'rpm', sound: 'ventilation' },
  { key: 'temp',       label: 'Temperature',         unit: '°C',  sound: 'temperature' },
  { key: 'ecgBatt',    label: 'ECG device battery',  unit: '%',   sound: 'equipment' },
  { key: 'spo2Batt',   label: 'SpO2 sensor battery', unit: '%',   sound: 'equipment' },
  { key: 'mobileBatt', label: 'Phone battery',       unit: '%',   sound: 'equipment' },
];

// Connection conditions are detected by the card itself (they are about the
// absence of data, not a value) — their rules are fixed in number but fully
// configurable in priority / message / sound.
export const TECH_CONDITIONS = {
  lead_off:     { name: 'Electrodes detached',            text: 'Electrodes detached' },
  device_off:   { name: 'ECG device not sending',         text: 'ECG device not sending (Bluetooth off / out of range)' },
  disconnected: { name: 'Phone disconnected from cloud',  text: 'Phone disconnected from the cloud' },
};

// ───────────────────────── defaults ─────────────────────────
const L = (id, label, param, op, value, delaySec, priority, extra) => Object.assign({
  id, kind: 'limit', group: 'vital', enabled: true, label, param, op, value, delaySec, priority,
  sound: (PARAM_OPTIONS.find(p => p.key === param) || {}).sound || 'general',
  repeatSec: PRIORITIES[priority].repeatDefault, maxSoundSec: 0,
}, extra || {});
const E = (id, label, event, priority, delaySec) => ({
  id, kind: 'event', group: 'arrhythmia', enabled: true, label, event, delaySec: delaySec || 0, priority,
  sound: 'cardiac', repeatSec: PRIORITIES[priority].repeatDefault, maxSoundSec: 0,
});
const T = (id, label, priority, delaySec, maxSoundSec) => ({
  id, kind: 'technical', group: 'technical', enabled: true, label, tech: id, delaySec, priority,
  sound: 'equipment', repeatSec: PRIORITIES[priority].repeatDefault, maxSoundSec,
});

export const DEFAULT_RULES = [
  // ── heart rate (adult defaults)
  L('hr_high_ext',  'EXTREME TACHYCARDIA', 'hr', 'gt', 150, 10, 'high'),
  L('hr_high',      'HIGH HR',             'hr', 'gt', 120, 10, 'medium'),
  L('hr_low',       'LOW HR',              'hr', 'lt', 50,  10, 'medium'),
  L('hr_low_ext',   'EXTREME BRADYCARDIA', 'hr', 'lt', 40,  10, 'high'),
  // ── SpO2 / resp / temp
  L('spo2_low_ext', 'SpO2 CRITICALLY LOW', 'spo2', 'lt', 88, 10, 'high'),
  L('spo2_low',     'LOW SpO2',            'spo2', 'lt', 92, 15, 'medium'),
  L('resp_high',    'HIGH RESP RATE',      'resp', 'gt', 30, 30, 'medium'),
  L('resp_low',     'LOW RESP RATE',       'resp', 'lt', 8,  30, 'medium'),
  L('temp_high',    'HIGH TEMPERATURE',    'temp', 'gt', 38.5, 60, 'low'),
  L('temp_low',     'LOW TEMPERATURE',     'temp', 'lt', 35,   60, 'low'),

  // ── arrhythmia events declared by the analysis stage (vitals.events)
  E('ev_asystole',   'ASYSTOLE',          'ASYSTOLE',    'high'),
  E('ev_vfib',       'VENTRICULAR FIB',   'VF',          'high'),
  E('ev_vt',         'VT',                'VT',          'high'),
  E('ev_standstill', 'V-STANDSTILL',      'VSTANDSTILL', 'high'),
  E('ev_pause',      'PAUSE',             'PAUSE',       'high'),
  E('ev_svt',        'SVT',               'SVT',         'medium'),
  E('ev_afib',       'AFIB',              'AFIB',        'medium'),
  E('ev_brady',      'BRADYCARDIA',       'BRADY',       'medium'),
  E('ev_pvc_run',    'PVC RUN',           'PVCRUN',      'medium'),
  E('ev_pvc',        'FREQUENT PVC',      'PVC',         'low'),

  // ── technical
  T('lead_off',     'LEAD OFF',     'medium', 5,  0),
  T('device_off',   'DEVICE OFF',   'medium', 0,  120),
  T('disconnected', 'DISCONNECTED', 'medium', 0,  60),
  // Low-battery reminders repeat every 2 min: a wearable that will stop
  // monitoring within the hour is actionable, and a single two-pulse blip
  // (the low-priority default of "once") is too easy to miss on a busy ward.
  L('batt_ecg_crit',   'ECG BATT CRITICAL',  'ecgBatt',    'lt', 10, 30, 'medium', { group: 'technical' }),
  L('batt_ecg',        'ECG BATT LOW',       'ecgBatt',    'lt', 20, 30, 'low',    { group: 'technical', repeatSec: 120 }),
  L('batt_spo2',       'SPO2 BATT LOW',      'spo2Batt',   'lt', 20, 30, 'low',    { group: 'technical', repeatSec: 120 }),
  L('batt_phone_crit', 'PHONE BATT CRITICAL','mobileBatt', 'lt', 10, 30, 'medium', { group: 'technical' }),
  L('batt_phone',      'PHONE BATT LOW',     'mobileBatt', 'lt', 20, 30, 'low',    { group: 'technical', repeatSec: 120 }),
];

export function defaultConfig() {
  return { version: CONFIG_VERSION, rev: 0, updatedBy: '', updatedAt: null,
    rules: DEFAULT_RULES.map(r => Object.assign({}, r)) };
}

// ───────────────────────── validation ─────────────────────────
const num = (v, d) => { const n = Number(v); return isNaN(n) ? d : n; };
export const normEvent = (e) => String(e == null ? '' : e).toUpperCase().replace(/[^A-Z0-9]/g, '');

/** Clamp a rule into a valid, IEC-conformant shape. Returns a new object. */
export function sanitizeRule(r) {
  const priority = PRIORITIES[r.priority] ? r.priority : 'medium';
  const P = PRIORITIES[priority];
  let repeatSec = num(r.repeatSec, P.repeatDefault);
  if (priority === 'low') {
    // low priority: no repeat (0) or an interburst interval above 15 s
    repeatSec = repeatSec <= 0 ? 0 : Math.max(P.repeat[0], Math.min(P.repeat[1], repeatSec));
  } else {
    repeatSec = Math.max(P.repeat[0], Math.min(P.repeat[1], repeatSec || P.repeatDefault));
  }
  const out = {
    id: String(r.id || ('r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6))),
    kind: ['limit', 'event', 'technical'].indexOf(r.kind) >= 0 ? r.kind : 'limit',
    group: ['vital', 'arrhythmia', 'technical'].indexOf(r.group) >= 0 ? r.group : 'vital',
    enabled: r.enabled !== false,
    audible: r.audible !== false,
    label: String(r.label || '').replace(/[ -<>]/g, '').trim().slice(0, 32).toUpperCase() || 'ALARM',
    delaySec: Math.max(0, Math.min(600, num(r.delaySec, 0))),
    priority: priority,
    sound: SOUNDS[r.sound] ? r.sound : 'general',
    repeatSec: repeatSec,
    maxSoundSec: Math.max(0, Math.min(3600, num(r.maxSoundSec, 0))),
  };
  if (out.kind === 'limit') {
    out.param = PARAM_OPTIONS.some(p => p.key === r.param) ? r.param : 'hr';
    out.op = r.op === 'lt' ? 'lt' : 'gt';
    out.value = num(r.value, 0);
  } else if (out.kind === 'event') {
    out.event = normEvent(r.event).slice(0, 24) || 'EVENT';
    out.group = 'arrhythmia';
  } else {
    out.tech = TECH_CONDITIONS[r.tech] ? r.tech : 'lead_off';
    out.id = out.tech;                       // fixed identity: the card looks these up by id
    out.group = 'technical';
  }
  return out;
}

export function sanitizeConfig(cfg) {
  const base = defaultConfig();
  if (!cfg || !Array.isArray(cfg.rules)) return base;
  const rules = cfg.rules.slice(0, 120).map(sanitizeRule);
  // The three connection rules must always exist (a user can disable, not delete).
  Object.keys(TECH_CONDITIONS).forEach(id => {
    if (!rules.some(r => r.kind === 'technical' && r.tech === id)) {
      rules.push(sanitizeRule(DEFAULT_RULES.find(d => d.id === id)));
    }
  });
  return { version: CONFIG_VERSION, rev: num(cfg.rev, 0), updatedBy: String(cfg.updatedBy || ''),
    updatedAt: cfg.updatedAt || null, rules: rules };
}

// ───────────────────────── store ─────────────────────────
let current = null;
const listeners = new Set();

function readLocal() {
  try { return JSON.parse(window.localStorage.getItem(LS_KEY)); } catch (e) { return null; }
}
function writeLocal(cfg) {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch (e) { /* storage blocked */ }
}

export function getAlarmConfig() {
  if (!current) current = sanitizeConfig(readLocal());
  return current;
}
export function onAlarmConfigChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function apply(cfg) {
  current = sanitizeConfig(cfg);
  writeLocal(current);
  listeners.forEach(fn => fn(current));
  return current;
}

/**
 * What the Repeat value will become on save, and why — so the settings panel
 * can SAY it instead of silently changing the number the user typed.
 */
export function repeatRule(priority, typed) {
  const P = PRIORITIES[priority] || PRIORITIES.medium;
  const n = Number(typed);
  if (priority === 'low') {
    if (!(n > 0)) return { value: 0, text: 'sounds once' };
    if (n < P.repeat[0]) return { value: P.repeat[0], text: `low priority cannot repeat faster than ${P.repeat[0]} s — use Medium for 2.5–30 s`, clamped: true };
    if (n > P.repeat[1]) return { value: P.repeat[1], text: `maximum ${P.repeat[1]} s`, clamped: true };
    return { value: n, text: `every ${n} s` };
  }
  if (!(n > 0)) return { value: P.repeatDefault, text: `${P.label} priority must repeat — will use ${P.repeatDefault} s`, clamped: true };
  if (n < P.repeat[0]) return { value: P.repeat[0], text: `minimum for ${P.label} is ${P.repeat[0]} s`, clamped: true };
  if (n > P.repeat[1]) return { value: P.repeat[1], text: `maximum for ${P.label} is ${P.repeat[1]} s — use Low for longer gaps`, clamped: true };
  return { value: n, text: `every ${n} s` };
}

/** Pull the shared configuration from the server (falls back to the local copy). */
export function loadAlarmConfig() {
  return axios.get(`${liveApi}api/live/alarm-config`)
    .then(res => (res.data && Array.isArray(res.data.rules) ? apply(res.data) : getAlarmConfig()))
    .catch(() => getAlarmConfig());        // older backend / offline: keep the local one
}

/** Save for everyone. Always applied locally; `shared` says whether the server took it. */
export function saveAlarmConfig(cfg) {
  const clean = apply(Object.assign({}, cfg, { updatedAt: new Date().toISOString() }));
  const url = `${liveApi}api/live/alarm-config`;
  const body = { rules: clean.rules };
  // POST, falling back to PUT for the first backend build (PUT-only route).
  return axios.post(url, body)
    .catch(err => { if (err && err.response && err.response.status === 404) return axios.put(url, body); throw err; })
    .then(res => { if (res.data && Array.isArray(res.data.rules)) apply(res.data); return { shared: true }; })
    .catch(() => ({ shared: false }));
}
